import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import {
  Search as SearchIcon,
  History as HistoryIcon,
  AccountBalance as CurrentIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import apiClient from '../../../../../../utils/apiClient';

const SchoolCard = ({ school, label, color, icon }) => {
  if (!school) return null;
  return (
    <Card variant="outlined" sx={{ borderColor: `${color}.main`, borderWidth: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {icon}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: `${color}.main` }}>
            {label}
          </Typography>
          <Chip
            label={school.status || 'active'}
            size="small"
            color={school.status === 'closed' ? 'default' : 'success'}
          />
        </Box>
        <Typography variant="h6">{school.display_name || school.name}</Typography>
        <Typography variant="body2" color="text.secondary">
          NCES: {school.ncessch}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          District: {school.district}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {school.city}, {school.state}
        </Typography>
      </CardContent>
    </Card>
  );
};

const SchoolSearchPanel = ({ label, color, icon, selectedSchool, onSelect, disabledUuid }) => {
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState([]);
  const [searchError, setSearchError] = useState(null);
  const { enqueueSnackbar } = useSnackbar();

  const handleSearch = async () => {
    if (!query.trim()) return;
    setIsSearching(true);
    setSearchError(null);
    setResults([]);

    try {
      const data = await apiClient.get('/v2/nces/schools/search', {
        params: { q: query.trim(), limit: 10 },
      });
      setResults(data.schools || []);
      if ((data.schools || []).length === 0) {
        setSearchError('No schools found. Try a different name or NCES ID.');
      }
    } catch (err) {
      setSearchError('Search failed. Please try again.');
      enqueueSnackbar('School search failed', { variant: 'error' });
    } finally {
      setIsSearching(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleSelect = (school) => {
    onSelect(school);
    setResults([]);
    setQuery('');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        {icon}
        <Typography variant="h6" sx={{ color: `${color}.main`, fontWeight: 700 }}>
          {label}
        </Typography>
      </Box>

      {selectedSchool ? (
        <Box>
          <SchoolCard school={selectedSchool} label={label} color={color} icon={icon} />
          <Button
            size="small"
            sx={{ mt: 1 }}
            onClick={() => onSelect(null)}
          >
            Change Selection
          </Button>
        </Box>
      ) : (
        <Box>
          <TextField
            fullWidth
            size="small"
            label="Search by name or NCES ID"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSearching}
            InputProps={{
              endAdornment: isSearching ? (
                <CircularProgress size={20} />
              ) : (
                <SearchIcon
                  sx={{ cursor: 'pointer', color: 'action.active' }}
                  onClick={handleSearch}
                />
              ),
            }}
            sx={{ mb: 1 }}
          />

          {searchError && (
            <Alert severity="warning" sx={{ mb: 1 }}>
              {searchError}
            </Alert>
          )}

          {results.length > 0 && (
            <Card variant="outlined" sx={{ maxHeight: 280, overflow: 'auto' }}>
              <List dense>
                {results.map((school) => {
                  const isDisabled = school.uuid === disabledUuid;
                  return (
                    <ListItem key={school.uuid} disablePadding>
                      <ListItemButton
                        disabled={isDisabled}
                        onClick={() => handleSelect(school)}
                      >
                        <ListItemText
                          primary={school.display_name || school.name}
                          secondary={`NCES: ${school.ncessch} | ${school.city}, ${school.state} | ${school.district}`}
                        />
                        {isDisabled && (
                          <Chip label="Already selected" size="small" />
                        )}
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Card>
          )}
        </Box>
      )}
    </Box>
  );
};

const SchoolSelectionStep = ({ workflowData, onDataUpdate, isValid }) => {
  const handleSelectSource = (school) => {
    onDataUpdate({ sourceSchool: school });
  };

  const handleSelectSurvivor = (school) => {
    onDataUpdate({ survivorSchool: school });
  };

  const sameSchoolError =
    workflowData.sourceSchool &&
    workflowData.survivorSchool &&
    workflowData.sourceSchool.uuid === workflowData.survivorSchool.uuid;

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <SearchIcon color="primary" />
        Select Schools to Merge
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Select the <strong>source</strong> (historical school that will be deactivated) and the{' '}
        <strong>survivor</strong> (current school that keeps its NCES ID and receives all historical data).
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle>When to use this workflow</AlertTitle>
        Use this when the same physical school received a new NCES ID — typically after a district
        transfer. The source school should have the older enrollment history; the survivor should be
        the currently active school with the new NCES ID.
      </Alert>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <SchoolSearchPanel
            label="Source School (historical, will be deactivated)"
            color="warning"
            icon={<HistoryIcon color="warning" />}
            selectedSchool={workflowData.sourceSchool}
            onSelect={handleSelectSource}
            disabledUuid={workflowData.survivorSchool?.uuid}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <SchoolSearchPanel
            label="Survivor School (keeps its NCES ID)"
            color="success"
            icon={<CurrentIcon color="success" />}
            selectedSchool={workflowData.survivorSchool}
            onSelect={handleSelectSurvivor}
            disabledUuid={workflowData.sourceSchool?.uuid}
          />
        </Grid>
      </Grid>

      {sameSchoolError && (
        <Alert severity="error" sx={{ mt: 2 }}>
          Source and survivor must be different schools.
        </Alert>
      )}

      {isValid && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Alert severity="success">
            <AlertTitle>Both schools selected</AlertTitle>
            <strong>{workflowData.sourceSchool.display_name || workflowData.sourceSchool.name}</strong>
            {' '}(NCES {workflowData.sourceSchool.ncessch}) will be merged into{' '}
            <strong>{workflowData.survivorSchool.display_name || workflowData.survivorSchool.name}</strong>
            {' '}(NCES {workflowData.survivorSchool.ncessch}).
            Click <strong>Next</strong> to review what will be transferred.
          </Alert>
        </Box>
      )}

      {!workflowData.sourceSchool && !workflowData.survivorSchool && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Box component="ul" sx={{ m: 0, pl: 2 }}>
            <li>Search for the historical school on the left and select it as the source</li>
            <li>Search for the current school on the right and select it as the survivor</li>
            <li>Click Next to validate that their enrollment years do not overlap</li>
          </Box>
        </Alert>
      )}
    </Box>
  );
};

export default SchoolSelectionStep;
